<script>
import { specialtiesAPI } from '../../../api/specialties';
import { attendanceTypesAPI } from '../../../api/attendanceTypes';
import { attendancePricesAPI } from '../../../api/attendancePrices';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

export default {
  name: 'AttendancePricesView',
  data() {
    return {
      specialties: [],
      attendanceTypes: [],
      // Grid state keyed by `${specialtyId}:${attendanceTypeId}` — the same key the
      // backend uses — holding the raw string the user typed.
      cells: {},
      loading: true,
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    hasGrid() {
      return this.specialties.length > 0 && this.attendanceTypes.length > 0;
    },
  },
  async mounted() {
    await this.fetchGrid();
  },
  methods: {
    cellKey(specialtyId, attendanceTypeId) {
      return `${specialtyId}:${attendanceTypeId}`;
    },
    async fetchGrid() {
      this.loading = true;
      try {
        const [specialties, types, prices] = await Promise.all([
          specialtiesAPI.getAll(this.accountId, true),
          attendanceTypesAPI.getAll(this.accountId, true),
          attendancePricesAPI.getAll(this.accountId),
        ]);
        this.specialties = specialties.data || [];
        this.attendanceTypes = types.data || [];
        const cells = {};
        (prices.data || []).forEach(price => {
          cells[this.cellKey(price.specialtyId, price.attendanceTypeId)] =
            this.toInput(price.priceValue);
        });
        this.cells = cells;
      } catch {
        useAlert(this.$t('ATTENDANCE_PRICES.ERRORS.FETCH'));
      } finally {
        this.loading = false;
      }
    },
    // The backend returns a plain decimal; the input shows it with a comma because
    // that is what a Brazilian user types.
    toInput(value) {
      if (value === null || value === undefined || value === '') return '';
      return String(value).replace('.', ',');
    },
    // '' / whitespace means "no price for this combination" and clears the row.
    // Anything that isn't a number is rejected before saving, never sent as zero.
    toNumber(raw) {
      if (raw === null || raw === undefined) return null;
      const trimmed = String(raw).trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed.replace(/\./g, '').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : NaN;
    },
    async saveGrid() {
      if (this.isSaving) return;
      const items = [];
      for (const specialty of this.specialties) {
        for (const type of this.attendanceTypes) {
          const raw = this.cells[this.cellKey(specialty.id, type.id)];
          const parsed = this.toNumber(raw);
          if (Number.isNaN(parsed)) {
            useAlert(
              this.$t('ATTENDANCE_PRICES.ERRORS.INVALID_VALUE', {
                specialty: specialty.name,
                type: type.description,
              })
            );
            return;
          }
          if (parsed !== null && parsed < 0) {
            useAlert(
              this.$t('ATTENDANCE_PRICES.ERRORS.NEGATIVE_VALUE', {
                specialty: specialty.name,
                type: type.description,
              })
            );
            return;
          }
          items.push({
            specialtyId: specialty.id,
            attendanceTypeId: type.id,
            priceValue: parsed,
          });
        }
      }
      this.isSaving = true;
      try {
        await attendancePricesAPI.saveAll(this.accountId, items);
        useAlert(this.$t('ATTENDANCE_PRICES.SAVED'));
        await this.fetchGrid();
      } catch {
        useAlert(this.$t('ATTENDANCE_PRICES.ERRORS.SAVE'));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full px-6 py-6 overflow-y-auto bg-slate-50">
    <div class="flex items-center justify-between pb-2">
      <h1 class="text-2xl font-bold text-slate-800">{{ $t('ATTENDANCE_PRICES.TITLE') }}</h1>
      <button
        :disabled="isSaving || loading || !hasGrid"
        class="flex items-center gap-2 px-4 py-2 font-medium text-white transition-colors rounded-md shadow-sm bg-fluxi-primary hover:bg-fluxi-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="saveGrid"
      >
        <span
          v-if="isSaving"
          class="inline-block w-4 h-4 border-2 rounded-full border-white/40 border-t-white animate-spin"
        />
        {{ isSaving ? $t('ATTENDANCE_PRICES.SAVING') : $t('ATTENDANCE_PRICES.SAVE_BUTTON') }}
      </button>
    </div>

    <p class="mb-6 text-sm text-slate-500">{{ $t('ATTENDANCE_PRICES.SUBTITLE') }}</p>

    <div v-if="loading" class="p-8 text-sm text-center bg-white border rounded-lg shadow-sm text-slate-500 border-slate-200">
      {{ $t('ATTENDANCE_PRICES.LOADING') }}
    </div>

    <div v-else-if="!hasGrid" class="p-8 text-sm text-center bg-white border rounded-lg shadow-sm text-slate-500 border-slate-200">
      {{ $t('ATTENDANCE_PRICES.EMPTY') }}
    </div>

    <div v-else class="overflow-x-auto bg-white border rounded-lg shadow-sm border-slate-200">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-slate-50/50">
          <tr>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase text-slate-500">
              {{ $t('ATTENDANCE_PRICES.TABLE.SPECIALTY') }}
            </th>
            <th
              v-for="type in attendanceTypes"
              :key="type.id"
              class="px-4 py-4 text-xs font-semibold tracking-wider text-center uppercase text-slate-500 whitespace-nowrap"
            >
              {{ type.description }}
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-for="specialty in specialties" :key="specialty.id" class="transition-colors hover:bg-slate-50/50">
            <td class="px-6 py-3 text-sm font-medium text-slate-700 whitespace-nowrap">{{ specialty.name }}</td>
            <td v-for="type in attendanceTypes" :key="type.id" class="px-4 py-3 text-sm">
              <div class="flex items-center gap-1">
                <span class="text-xs text-slate-400">R$</span>
                <input
                  v-model="cells[cellKey(specialty.id, type.id)]"
                  type="text"
                  inputmode="decimal"
                  placeholder="—"
                  class="w-24 px-2 py-1 text-sm text-right border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="mt-4 text-xs text-slate-500">{{ $t('ATTENDANCE_PRICES.BLANK_HINT') }}</p>
  </div>
</template>
